import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Flex } from 'antd'
import { AUTH_TOKEN } from '../../constants/common'
import { postRequest } from '../../services/base'
import { setLocalStorageData } from '../../utils/LocalStorageAccess'
import { safeNextPath } from './reasons'
import LoginCard from './LoginCard'
import SignInForm from './SignInForm'

/**
 * Sign-in with an AT account (hosts without Cognito): email and password only.
 * There is no self-service reset for these accounts - an administrator sets
 * the password - so unlike the Cognito page there is no "Forgot password?".
 */
const NativeLogin = ({ notice = null }) => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const signIn = async ({ email, password }) => {
    setBusy(true)
    setError(null)
    const formData = new FormData()
    formData.append('email', email)
    formData.append('password', password)
    const res = await postRequest('/api_auth/', formData)
    if (res?.status === false || !res?.token) {
      setBusy(false)
      setError({ type: 'error', message: intl.formatMessage({ id: 'auth.loginFailed' }) })
      return
    }
    setError({ type: 'success', message: intl.formatMessage({ id: 'auth.loginSuccess' }) })
    setLocalStorageData(AUTH_TOKEN, res.token)
    // A full load, not a route change: the layout reads the token once on mount.
    window.location.href = safeNextPath(searchParams.get('next'))
  }

  return (
    <LoginCard
      title={intl.formatMessage({ id: 'auth.signin' })}
      prompt={intl.formatMessage({ id: 'auth.loginPrompt' })}
      notice={notice && !error && intl.formatMessage({ id: notice })}
      error={error}
    >
      {({ alerts }) => (
        <Flex vertical={true} gap="middle">
          {alerts}
          <SignInForm busy={busy} onSubmit={signIn} />
        </Flex>
      )}
    </LoginCard>
  )
}

NativeLogin.propTypes = {
  // An i18n id shown as the page opens, e.g. after logout
  notice: PropTypes.string,
}

export default NativeLogin
